import {
  activate,
  ensureInitialized,
  fetchAndActivate,
  fetchConfig,
  getRemoteConfig,
  getValue,
  RemoteConfig,
} from 'firebase/remote-config';
import { getFirebaseApp } from './app';
import { isFirebaseConfigured } from './config';

export const MENU_MANAGEMENT_LIVE_CONFIG_WEB_KEY =
  'menu_management_live_config_web';
export const MENU_MANAGEMENT_SANDBOX_CONFIG_WEB_KEY =
  'menu_management_sandbox_config_web';

/** Production uses live web JSON; development uses sandbox web parameter. */
export function getMenuManagementRemoteConfigParamKey(): string {
  return process.env.REACT_APP_NODE_ENV === 'production'
    ? MENU_MANAGEMENT_LIVE_CONFIG_WEB_KEY
    : MENU_MANAGEMENT_SANDBOX_CONFIG_WEB_KEY;
}

let remoteConfigInstance: RemoteConfig | null = null;
let fetchPromise: Promise<boolean> | null = null;

function getMinimumFetchIntervalMillis(): number {
  const override = process.env.REACT_APP_REMOTE_CONFIG_MIN_FETCH_MS;
  if (override !== undefined && override !== '') {
    const parsed = Number(override);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  // Default: fetch published values on every page load / refresh.
  return 0;
}

function getRc(): RemoteConfig | null {
  if (!isFirebaseConfigured()) {
    return null;
  }
  if (!remoteConfigInstance) {
    const rc = getRemoteConfig(getFirebaseApp());
    rc.settings.minimumFetchIntervalMillis = getMinimumFetchIntervalMillis();
    remoteConfigInstance = rc;
  } else {
    remoteConfigInstance.settings.minimumFetchIntervalMillis =
      getMinimumFetchIntervalMillis();
  }
  return remoteConfigInstance;
}

async function fetchFreshAndActivate(rc: RemoteConfig): Promise<boolean> {
  rc.settings.minimumFetchIntervalMillis = getMinimumFetchIntervalMillis();
  await ensureInitialized(rc);
  try {
    await fetchConfig(rc);
    return await activate(rc);
  } catch {
    return fetchAndActivate(rc);
  }
}

export async function fetchAndActivateRemoteConfig(
  options: { force?: boolean } = {},
): Promise<boolean> {
  const rc = getRc();
  if (!rc) {
    return false;
  }
  if (options.force) {
    fetchPromise = null;
  }
  if (!fetchPromise) {
    fetchPromise = fetchFreshAndActivate(rc).finally(() => {
      fetchPromise = null;
    });
  }
  return fetchPromise;
}

/** String parameter (define the key in Firebase Console → Remote Config → Parameters). */
export function getRemoteConfigString(key: string, defaultValue = ''): string {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asString();
}

export function getRemoteConfigBoolean(
  key: string,
  defaultValue = false,
): boolean {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asBoolean();
}

export function getRemoteConfigNumber(key: string, defaultValue = 0): number {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asNumber();
}
